import { lazy, Suspense, useCallback, useEffect, useId, useRef, useState } from 'react';
import { useAssistenteIAChat } from '@/modules/assistente-ia/hooks/useAssistenteIAChat';
import SparkleIcon from '@/modules/assistente-ia/components/SparkleIcon';
import './AssistenteIAWidget.scss';

const AssistenteIAChatThread = lazy(() =>
    import('@/modules/assistente-ia/components/AssistenteIAChatThread')
);

export default function AssistenteIAWidget() {
    const [aberto, setAberto] = useState(false);
    const [expandido, setExpandido] = useState(false);
    const chat = useAssistenteIAChat();
    const painelId = useId();
    const fabRef = useRef<HTMLButtonElement>(null);

    // Estável via `useCallback` (não inline no JSX) — vira dependência do `useMemo` que monta os
    // componentes de markdown em AssistenteIAChatThread.tsx; uma referência nova a cada render
    // (setExpandido em si já é estável, é a arrow function em volta que precisa ser) invalidava
    // esse useMemo a CADA tecla digitada, o que por sua vez quebrava o `React.memo` de
    // `MensagemLinha` pra TODAS as mensagens já enviadas — reprocessando o markdown da conversa
    // inteira a cada tecla, ficando mais lento quanto maior o histórico.
    const expandirPainel = useCallback(() => setExpandido(true), []);

    useEffect(() => {
        if (!aberto) {
            return;
        }

        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                setAberto(false);
                fabRef.current?.focus();
            }
        };

        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [aberto]);

    return (
        <>
            <div
                id={painelId}
                className="assistente-ia-widget__painel"
                data-aberto={aberto}
                data-expandido={expandido}
                role="dialog"
                aria-label="Assistente de IA"
                aria-hidden={!aberto}
                inert={!aberto ? true : undefined}
            >
                <header className="assistente-ia-widget__cabecalho">
                    <span className="assistente-ia-widget__avatar" aria-hidden="true">
                        <SparkleIcon className="assistente-ia-widget__avatar-logo" />
                    </span>

                    <h2 className="assistente-ia-widget__titulo">Ugarqui IA</h2>

                    <button
                        type="button"
                        className="assistente-ia-widget__expandir"
                        onClick={() => setExpandido((atual) => !atual)}
                        aria-label={expandido ? 'Recolher' : 'Expandir'}
                        aria-pressed={expandido}
                    >
                        <i
                            className={`fa-solid ${expandido ? 'fa-compress' : 'fa-expand'}`}
                            aria-hidden="true"
                        />
                    </button>

                    <button
                        type="button"
                        className="assistente-ia-widget__fechar"
                        onClick={() => {
                            setAberto(false);
                            fabRef.current?.focus();
                        }}
                        aria-label="Fechar assistente"
                    >
                        <i className="fa-solid fa-xmark" aria-hidden="true" />
                    </button>
                </header>

                {aberto && (
                    <Suspense fallback={null}>
                        <AssistenteIAChatThread chat={chat} onExpandirPainel={expandirPainel} />
                    </Suspense>
                )}
            </div>

            <button
                ref={fabRef}
                type="button"
                className="assistente-ia-widget__fab"
                onClick={() => setAberto((atual) => !atual)}
                aria-expanded={aberto}
                aria-controls={painelId}
                aria-label={aberto ? 'Fechar assistente de IA' : 'Abrir assistente de IA'}
            >
                <SparkleIcon className="assistente-ia-widget__fab-logo" />
            </button>
        </>
    );
}